import {Card} from 'platform/components';
import {Grid, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {FinancialProductMoney, FinancialProductUnavailableReason} from '@dms/api/sales';
import {FINANCIAL_PRODUCT_TYPE_ICONS} from '@dms/features/financial-products';
import i18n from '@dms/i18n';

import {parseDate, suffixTestId, type Nullish, type TestIdProps} from 'shared/web';

import type {
  FinancialProductPreferenceUpdatedByViewModel,
  FinancialProductPreferenceViewModel,
} from '../utils/getFinancialProductPreferenceViewModel';

const updatedByCopy = (updatedBy: FinancialProductPreferenceUpdatedByViewModel | null) =>
  match(updatedBy)
    .with({kind: 'CUSTOMER'}, () => i18n.t('page.businessCase.financialProducts.updatedByCustomer'))
    .with({kind: 'SELLER'}, ({sellerName}) =>
      i18n.t('page.businessCase.financialProducts.updatedBySeller', {seller: sellerName})
    )
    .with({kind: 'REMOVED_SELLER'}, () =>
      i18n.t('page.businessCase.financialProducts.updatedByRemovedSeller')
    )
    .otherwise(() => null);

const UNAVAILABLE_REASON_LABEL_KEY: Record<FinancialProductUnavailableReason, string> = {
  NOT_YET_VALID: 'financialProducts.unavailable.not_yet_valid',
  EXPIRED: 'financialProducts.unavailable.expired',
  ARCHIVED: 'financialProducts.unavailable.archived',
  DELETED: 'financialProducts.unavailable.deleted',
  MISSING_CONFIGURATION: 'financialProducts.unavailable.missing_configuration',
  MISSING_PROVIDER: 'financialProducts.unavailable.missing_provider',
  MISSING_VEHICLE_PRICE: 'financialProducts.unavailable.missing_vehicle_price',
  UNSUPPORTED_TERM: 'financialProducts.unavailable.unsupported_term',
  UNSUPPORTED_DOWN_PAYMENT: 'financialProducts.unavailable.unsupported_down_payment',
  UNSUPPORTED_BALLOON_PAYMENT: 'financialProducts.unavailable.unsupported_balloon_payment',
  UNSUPPORTED_TARGET_GROUP: 'financialProducts.unavailable.unsupported_target_group',
  MISSING_MATRIX_CELL: 'financialProducts.unavailable.missing_matrix_cell',
};

const formatPercentageAndMoney = (percentage: number | Nullish, amount: string | null) =>
  isNotNil(percentage) ? `${percentage} %${isNotNil(amount) ? ` · ${amount}` : ''}` : null;

interface ValueProps extends TestIdProps {
  label: string;
  value: string | null;
}

function Value(props: ValueProps) {
  if (isNil(props.value)) {
    return null;
  }

  return (
    <VStack spacing={1} data-testid={suffixTestId('value', props)}>
      <Text size="small" color="secondary">
        {props.label}
      </Text>
      <Text size="small" alternative>
        {props.value}
      </Text>
    </VStack>
  );
}

interface FinancialProductPreferenceSummaryProps extends TestIdProps {
  onOpenCalculator?: () => void;
  viewModel: FinancialProductPreferenceViewModel;
  preferenceUpdatedAt: string | Nullish;
}

export function FinancialProductPreferenceSummary(props: FinancialProductPreferenceSummaryProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();
  const offer = props.viewModel.offer;
  const calculation = offer?.availability === 'AVAILABLE' ? offer.calculation : null;

  const formatMoney = (money: FinancialProductMoney | Nullish) =>
    isNotNil(money) ? formatCurrency(Number(money.amount), money.currency) : null;

  const status = match(props.viewModel)
    .with({isSnapshot: true}, () => i18n.t('page.businessCase.financialProducts.signedValues'))
    .with({updatedBy: Pattern.nonNullable}, ({updatedBy}) => updatedByCopy(updatedBy))
    .with({isPrimaryOption: true}, () =>
      i18n.t('page.businessCase.financialProducts.primaryOption')
    )
    .otherwise(() => null);

  // An older backend or a contract signed before the costs were published has no breakdown.
  const hasLeasingServicesBreakdown =
    isNotNilOrEmpty(offer?.leasingCosts?.monthlyServices) &&
    isNotNil(calculation?.leasingTotalMonthlyPayment);

  const targetGroup = match(calculation?.leasingTargetGroup)
    .with('PRIVATE', () => i18n.t('page.vehicle.financingAndLeasing.private'))
    .with('COMMERCIAL', () => i18n.t('page.vehicle.financingAndLeasing.commercial'))
    .otherwise(() => null);

  return (
    <Card
      actions={
        props.onOpenCalculator
          ? [
              {
                type: 'button',
                variant: 'link',
                leftIcon: 'image/edit',
                title: i18n.t('page.businessCase.financialProducts.openCalculator'),
                onClick: props.onOpenCalculator,
              },
            ]
          : []
      }
      variant="inlineGrey"
      title={i18n.t('page.businessCase.financialProducts.customerPreference')}
      data-testid={suffixTestId('financialProductPreference', props)}
    >
      <VStack spacing={4}>
        <Show when={props.viewModel.hasNoProducts}>
          <Text color="secondary" data-testid={suffixTestId('noProducts', props)}>
            {i18n.t('page.businessCase.financialProducts.noProducts')}
          </Text>
        </Show>

        <Show when={!props.viewModel.hasNoProducts}>
          <HStack spacing={3} align="center" wrap data-testid={suffixTestId('heading', props)}>
            <HStack spacing={1} align="center">
              <Show when={props.viewModel.productType}>
                {(productType) => (
                  <Icon
                    value={FINANCIAL_PRODUCT_TYPE_ICONS[productType]}
                    size={4}
                    color="general.accent"
                    data-testid={suffixTestId('productTypeIcon', props)}
                  />
                )}
              </Show>
              <Text alternative>{props.viewModel.productName}</Text>
            </HStack>
            <Show when={isNotNil(status)}>
              <Text size="small" color="secondary" data-testid={suffixTestId('status', props)}>
                {status}
              </Text>
            </Show>
            <Show when={props.preferenceUpdatedAt}>
              <Text size="small" color="secondary" data-testid={suffixTestId('updatedAt', props)}>
                {isNotNil(props.preferenceUpdatedAt)
                  ? `· ${i18n.t('general.labels.lastUpdated')}: ${formatDateTime('dateTimeShort', parseDate(props.preferenceUpdatedAt))}`
                  : null}
              </Text>
            </Show>
          </HStack>

          <Show when={offer?.availability === 'UNAVAILABLE'}>
            <Text size="small" color="danger" data-testid={suffixTestId('unavailable', props)}>
              {i18n.t(
                offer?.availability === 'UNAVAILABLE'
                  ? UNAVAILABLE_REASON_LABEL_KEY[offer.unavailableReason]
                  : 'financialProducts.unavailable.generic'
              )}
            </Text>
          </Show>

          <Show when={offer?.availability === 'AVAILABLE'}>
            <Grid columns={[1, 2, 4]} spacing={4}>
              <Value
                label={i18n.t('page.vehicle.financingAndLeasing.resultingMonthlyRate')}
                value={formatMoney(calculation?.monthlyPayment)}
                data-testid={suffixTestId('monthlyPayment', props)}
              />
              <Value
                label={i18n.t('page.vehicle.financingAndLeasing.term')}
                value={
                  isNotNil(calculation?.termMonths)
                    ? i18n.t('page.vehicle.financingAndLeasing.monthsValue', {
                        term: calculation.termMonths,
                      })
                    : null
                }
                data-testid={suffixTestId('term', props)}
              />
              <Value
                label={i18n.t('page.vehicle.financingAndLeasing.downPayment')}
                value={formatPercentageAndMoney(
                  calculation?.downPaymentPercentage,
                  formatMoney(calculation?.downPaymentAmount)
                )}
                data-testid={suffixTestId('downPayment', props)}
              />
              <Show when={props.viewModel.productType === 'BALLOON'}>
                <Value
                  label={i18n.t('page.vehicle.financingAndLeasing.balloonPayment')}
                  value={formatPercentageAndMoney(
                    calculation?.balloonPaymentPercentage,
                    formatMoney(calculation?.balloonPaymentAmount)
                  )}
                  data-testid={suffixTestId('balloonPayment', props)}
                />
              </Show>
              <Show when={props.viewModel.productType === 'LEASING'}>
                <Value
                  label={i18n.t('page.vehicle.financingAndLeasing.targetGroup')}
                  value={targetGroup}
                  data-testid={suffixTestId('targetGroup', props)}
                />
                <Value
                  label={i18n.t('entity.vehicle.labels.annualMileage')}
                  value={
                    isNotNil(calculation?.annualMileage)
                      ? i18n.t('page.vehicle.financingAndLeasing.mileagePerYear', {
                          mileage: formatNumber(calculation.annualMileage),
                        })
                      : null
                  }
                  data-testid={suffixTestId('annualMileage', props)}
                />
                <Value
                  label={i18n.t('page.vehicle.financingAndLeasing.downPayment')}
                  value={formatMoney(calculation?.leasingDownPayment)}
                  data-testid={suffixTestId('downPayment', props)}
                />
                <Show when={hasLeasingServicesBreakdown}>
                  <Value
                    label={i18n.t('page.vehicle.financingAndLeasing.monthlyServices')}
                    value={formatMoney(offer?.leasingCosts?.monthlyServicesTotal)}
                    data-testid={suffixTestId('monthlyServices', props)}
                  />
                  <Value
                    label={i18n.t('page.businessCase.financialProducts.totalMonthlyPayment')}
                    value={formatMoney(calculation?.leasingTotalMonthlyPayment)}
                    data-testid={suffixTestId('totalMonthlyPayment', props)}
                  />
                </Show>
              </Show>
            </Grid>
          </Show>
        </Show>
      </VStack>
    </Card>
  );
}
